/**
 * F-039 — Per-planet motion trail.
 *
 * For each active planet we keep a short rolling buffer of recent
 * positions and render them as a faded Line. The line opacity falls off
 * with age + clears when the planet is removed (merge / Sun absorption /
 * restart).
 *
 * One TrailSystem per scene; the spawn registry calls track(planet) and
 * forget(planet) so trails appear on launch and disappear cleanly.
 */

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  Line,
  LineBasicMaterial,
  type Scene,
} from "three";

import type { Planet } from "../game/planet.ts";

const TRAIL_POINTS = 18;
const MIN_SEGMENT_DIST_SQ = 0.04;

interface TrailHandle {
  planet: Planet;
  line: Line;
  geometry: BufferGeometry;
  material: LineBasicMaterial;
  positions: Float32Array;
  count: number;
}

export interface PlanetTrailSystem {
  track(planet: Planet): void;
  forget(planet: Planet): void;
  tick(): void;
  clear(): void;
}

export interface PlanetTrailOptions {
  scene: Scene;
}

export function createPlanetTrailSystem(
  options: PlanetTrailOptions,
): PlanetTrailSystem {
  const handles = new Map<number, TrailHandle>();

  const track = (planet: Planet): void => {
    if (handles.has(planet.id)) return;
    const positions = new Float32Array(TRAIL_POINTS * 3);
    const geometry = new BufferGeometry();
    geometry.setAttribute("position", new BufferAttribute(positions, 3));
    geometry.setDrawRange(0, 0);
    const material = new LineBasicMaterial({
      color: new Color(planet.tier.color),
      transparent: true,
      opacity: 0.55,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    const line = new Line(geometry, material);
    line.name = `trail-${planet.id}`;
    line.frustumCulled = false;
    options.scene.add(line);
    handles.set(planet.id, {
      planet,
      line,
      geometry,
      material,
      positions,
      count: 0,
    });
  };

  const forget = (planet: Planet): void => {
    const h = handles.get(planet.id);
    if (!h) return;
    options.scene.remove(h.line);
    h.geometry.dispose();
    h.material.dispose();
    handles.delete(planet.id);
  };

  const tick = (): void => {
    for (const h of handles.values()) {
      const px = h.planet.body.position.x;
      const py = h.planet.body.position.y;
      const pz = h.planet.body.position.z;
      // Skip writing duplicate samples when the planet is barely moving.
      if (h.count > 0) {
        const lastX = h.positions[(h.count - 1) * 3]!;
        const lastY = h.positions[(h.count - 1) * 3 + 1]!;
        const lastZ = h.positions[(h.count - 1) * 3 + 2]!;
        const dx = px - lastX;
        const dy = py - lastY;
        const dz = pz - lastZ;
        if (dx * dx + dy * dy + dz * dz < MIN_SEGMENT_DIST_SQ) continue;
      }
      if (h.count < TRAIL_POINTS) {
        h.positions[h.count * 3] = px;
        h.positions[h.count * 3 + 1] = py;
        h.positions[h.count * 3 + 2] = pz;
        h.count += 1;
      } else {
        // Slide the buffer left by one sample.
        h.positions.copyWithin(0, 3);
        const last = (TRAIL_POINTS - 1) * 3;
        h.positions[last] = px;
        h.positions[last + 1] = py;
        h.positions[last + 2] = pz;
      }
      h.geometry.setDrawRange(0, h.count);
      h.geometry.attributes["position"]!.needsUpdate = true;
    }
  };

  const clear = (): void => {
    for (const h of [...handles.values()]) forget(h.planet);
  };

  return { track, forget, tick, clear };
}

export const __testing = { TRAIL_POINTS };
